import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import EventNoteIcon from '@mui/icons-material/EventNote';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import SavingsIcon from '@mui/icons-material/Savings';
import { formatCurrencyWithDecimals } from 'src/utils/formatAmout';
import {
  selectContractActionsSummary,
  selectContractActionsQueryOptions
} from 'src/store/selectors/contractActionsSelectors';
import formatMonthYear from 'src/utils/formatMonthYear';
import useStyles from '../../capitalBalance/components/terminationKpiStyles';

const monthLabel = (month, year, language) => {
  if (!month || !year) return null;
  const date = new Date(year, month - 1, 1);
  return formatMonthYear(date, language);
};

const KpiCard = ({ icon, iconClass, label, value, note }) => {
  const { classes } = useStyles();
  return (
    <Box
      className={classes.card}
      sx={{ width: { md: 'calc((100% - 72px) / 4)' } }}
    >
      <Box className={clsx(classes.cardIcon, iconClass)}>{icon}</Box>
      <Box>
        <Typography className={classes.cardLabel} component="p" variant="body2">
          {label}
        </Typography>
        <Typography color="primary" component="p" variant="h2">
          {value}
        </Typography>
        {note && (
          <Typography component="p" variant="body2">
            {note}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

KpiCard.propTypes = {
  icon: PropTypes.node.isRequired,
  iconClass: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  note: PropTypes.node
};

KpiCard.defaultProps = {
  note: null
};

const ContractActionsKpiCards = () => {
  const { i18n } = useTranslation();
  const summary = useSelector(selectContractActionsSummary);
  const { month, year } = useSelector(selectContractActionsQueryOptions);
  const { classes } = useStyles();
  const activeMonth = monthLabel(month, year, i18n.language);

  return (
    <Box className={classes.cardsRow}>
      <KpiCard
        icon={<EventNoteIcon />}
        iconClass={classes.cardIconInfo}
        label="Eventos registrados"
        value={summary.totalEvents}
        note={
          activeMonth ? `Contratos con movimiento en ${activeMonth}.` : null
        }
      />
      <KpiCard
        icon={<AddBusinessIcon />}
        iconClass={classes.cardIconOk}
        label="Altas de contrato"
        value={formatCurrencyWithDecimals(summary.altaAmount)}
        note={`${summary.altaCount} contratos nuevos este mes.`}
      />
      <KpiCard
        icon={<TrendingDownIcon />}
        iconClass={classes.cardIconError}
        label="Terminaciones anticipadas"
        value={formatCurrencyWithDecimals(summary.terminationAnticipadaAmount)}
        note={`${summary.terminationAnticipadaCount} cierres antes de plazo natural.`}
      />
      <KpiCard
        icon={<SavingsIcon />}
        iconClass={classes.cardIconNeutral}
        label="Aportaciones de capital"
        value={formatCurrencyWithDecimals(summary.capitalAmount)}
        note="Suma de aportaciones con monto capturado."
      />
    </Box>
  );
};

export default ContractActionsKpiCards;
